import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Frame, FramePanel } from "@volca/ui/components/ui/frame";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Tabs, TabsList, TabsTrigger } from "@volca/ui/components/ui/tabs";
import { Text } from "@volca/ui/components/ui/text";
import { useDebounce } from "@/core/hooks/use-debounce";
import { DOCUMENT_STATUS } from "@/profitability/document-constants";
import type { ProfitabilityDocument } from "@/profitability/types";
import { reportError } from "@/profitability/utils";
import { BlankChips } from "./blank-chips";
import type { DocumentCitation } from "./citation";
import { PreviewTable } from "./preview-table";
import { useDocumentPreview } from "./query";
import { usePreviewTarget } from "./use-preview-target";

/**
 * The file as it was read: a tab per table when there are several, a search
 * over its rows, a chip per column with blank cells (which narrows the table
 * to those rows, where a missing figure in the report came from), and the
 * rows a page at a time. A citation opens its table and outlines its cell.
 */
export const DocumentPreview = ({
  businessId,
  document,
  citation = null,
}: {
  businessId: string;
  document: Pick<ProfitabilityDocument, "id" | "status" | "error">;
  citation?: DocumentCitation | null;
}) => {
  const citedSheet =
    citation?.sheet && /^\d+$/.test(citation.sheet)
      ? Number(citation.sheet)
      : null;
  const [sheet, setSheet] = useState<number | null>(citedSheet);
  const [search, setSearch] = useState("");
  const [blank, setBlank] = useState<string | null>(null);
  const query = useDebounce(search.trim(), 250);
  const ready = document.status === DOCUMENT_STATUS.ready;
  const preview = useDocumentPreview(businessId, document.id, {
    sheet,
    query,
    blank,
  });
  const first = preview.data?.pages[0];
  const rows = preview.data?.pages.flatMap((page) => page.rows) ?? [];
  const target = usePreviewTarget({
    // Only on the table it points into.
    citation: first?.sheet === (citedSheet ?? 0) ? citation : null,
    columns: first?.columns ?? [],
    rows,
    hasMore: !!preview.hasNextPage,
    loading: preview.isFetching,
    filtered: !!query || !!blank,
    loadMore: preview.fetchNextPage,
  });

  if (document.status === DOCUMENT_STATUS.pending)
    return <Text tone="muted">Still reading this file.</Text>;
  if (!ready)
    return (
      <Text tone="destructive">
        {document.error ?? "This file could not be read."}
      </Text>
    );
  if (preview.isPending) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (preview.isError || !first)
    return (
      <ErrorState
        illustration="compact"
        title="This file could not be shown"
        description={reportError(preview.error)}
        retrying={preview.isFetching}
      >
        <Button onClick={() => void preview.refetch()}>Try again</Button>
      </ErrorState>
    );

  if (first.kind === "text")
    return (
      <Frame spacing="sm" className="min-h-0 lg:flex-1">
        <FramePanel className="min-h-0 overflow-y-auto">
          <Text
            render={<pre />}
            className="font-sans [overflow-wrap:anywhere] whitespace-pre-wrap"
          >
            {first.text}
          </Text>
        </FramePanel>
      </Frame>
    );
  if (first.sheet === null)
    return <Text tone="muted">No tables were read from this file.</Text>;

  const chooseSheet = (index: number) => {
    setSheet(index);
    setBlank(null);
  };
  return (
    <Stack gap={3} align="stretch" className="min-w-0 lg:min-h-0 lg:flex-1">
      {first.sheets.length > 1 && (
        <Tabs
          value={String(first.sheet)}
          onValueChange={(value) => chooseSheet(Number(value))}
        >
          <TabsList variant="line" aria-label="Tables in this file">
            {first.sheets.map((item) => (
              <TabsTrigger key={item.index} value={String(item.index)}>
                {item.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      <Stack direction="row" gap={2} align="center" wrap>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search rows"
          aria-label="Search rows"
          className="w-64"
        />
        <BlankChips
          blanks={first.blanks}
          columns={first.columns}
          active={blank}
          onChange={setBlank}
        />
      </Stack>
      <PreviewTable
        columns={first.columns}
        rows={rows}
        total={first.total}
        rowsCount={
          first.sheets.find((item) => item.index === first.sheet)?.rows_count ??
          first.total
        }
        filtered={!!query || !!blank}
        blankColumn={blank}
        target={target}
        onShowAll={() => {
          setSearch("");
          setBlank(null);
        }}
        hasMore={!!preview.hasNextPage}
        loadingMore={preview.isFetchingNextPage}
        onLoadMore={() => void preview.fetchNextPage()}
      />
    </Stack>
  );
};
